import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * AddressAutocomplete - Google Places autocomplete for street address input.
 * 
 * Uses the NEW Google Places API (REST) directly — no Maps JavaScript SDK needed.
 * This avoids the deprecated google.maps.places.Autocomplete that was blocked 
 * for new customers as of March 2025.
 * 
 * Requires: "Places API (New)" enabled in Google Cloud Console.
 * 
 * Props:
 *   value        - current streetAddress value
 *   formData     - full form data object  
 *   setFormData  - form data setter
 *   className    - input className (pass your existing Tailwind classes)
 *   placeholder  - input placeholder text
 */

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// Debounce helper
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/**
 * Fetch autocomplete suggestions from Google Places API (New)
 */
async function fetchSuggestions(input) {
  if (!input || input.length < 3 || !GOOGLE_MAPS_API_KEY) return [];

  try {
    const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
      },
      body: JSON.stringify({
        input,
        includedPrimaryTypes: ['street_address', 'subpremise', 'premise'],
        includedRegionCodes: ['us'],
      }),
    });

    if (!res.ok) return [];
    const data = await res.json();
    return (data.suggestions || [])
      .filter(s => s.placePrediction)
      .map(s => ({
        placeId: s.placePrediction.placeId,
        text: s.placePrediction.text?.text || s.placePrediction.structuredFormat?.mainText?.text || '',
        secondary: s.placePrediction.structuredFormat?.secondaryText?.text || '',
      }));
  } catch (err) {
    console.warn('AddressAutocomplete: suggestion fetch failed', err);
    return [];
  }
}

/**
 * Fetch place details to get structured address components
 */
async function fetchPlaceDetails(placeId) {
  if (!placeId || !GOOGLE_MAPS_API_KEY) return null;

  try {
    const res = await fetch(`https://places.googleapis.com/v1/places/${placeId}`, {
      headers: {
        'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
        'X-Goog-FieldMask': 'addressComponents',
      },
    });

    if (!res.ok) return null;
    const data = await res.json();
    return parseAddressComponents(data.addressComponents || []);
  } catch (err) {
    console.warn('AddressAutocomplete: place details fetch failed', err);
    return null;
  }
}

/**
 * Parse address components into form fields
 */
function parseAddressComponents(components) {
  const get = (type) => components.find(c => c.types.includes(type));

  const streetNumber = get('street_number')?.longText || '';
  const route = get('route')?.longText || '';
  const city = get('locality')?.longText
    || get('sublocality_level_1')?.longText
    || get('neighborhood')?.longText
    || get('administrative_area_level_2')?.longText
    || '';
  const state = get('administrative_area_level_1')?.shortText || '';
  const zipCode = get('postal_code')?.longText || '';

  const streetAddress = [streetNumber, route].filter(Boolean).join(' ');
  return { streetAddress, city, state, zipCode };
}

export default function AddressAutocomplete({
  value,
  formData,
  setFormData,
  className = '',
  placeholder = 'Street Address'
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const skipNextFetch = useRef(false);

  const debouncedValue = useDebounce(value, 300);

  // Fetch suggestions when input changes
  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (!debouncedValue || debouncedValue.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    let cancelled = false;
    (async () => {
      const results = await fetchSuggestions(debouncedValue);
      if (!cancelled) {
        setSuggestions(results);
        setShowDropdown(results.length > 0);
        setActiveIndex(-1);
      }
    })();

    return () => { cancelled = true; };
  }, [debouncedValue]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Handle selecting a suggestion
  const handleSelect = useCallback(async (suggestion) => {
    setShowDropdown(false);
    setLoading(true);
    skipNextFetch.current = true;

    const parsed = await fetchPlaceDetails(suggestion.placeId);
    setLoading(false);

    if (parsed) {
      setFormData(prev => ({
        ...prev,
        streetAddress: parsed.streetAddress,
        city: parsed.city,
        state: parsed.state,
        zipCode: parsed.zipCode,
      }));
    } else {
      // Fallback: use the suggestion text directly
      setFormData(prev => ({
        ...prev,
        streetAddress: suggestion.text,
      }));
    }
  }, [setFormData]);

  // Handle typing
  const handleChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, streetAddress: e.target.value }));
  }, [setFormData]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!showDropdown || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  }, [showDropdown, suggestions, activeIndex, handleSelect]);

  return (
    <div ref={wrapperRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {showDropdown && suggestions.length > 0 && (
        <ul
          className="absolute z-[10000] left-0 right-0 mt-1 bg-white border-2 border-slate-200 rounded-xl shadow-lg overflow-hidden"
          role="listbox"
        >
          {suggestions.map((s, i) => (
            <li
              key={s.placeId}
              role="option"
              aria-selected={i === activeIndex}
              className={`px-4 py-3 cursor-pointer transition-colors text-sm ${
                i === activeIndex ? 'bg-blue-50' : 'hover:bg-slate-50'
              } ${i > 0 ? 'border-t border-slate-100' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(s);
              }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span className="font-medium text-slate-900">{s.text}</span>
              {s.secondary && (
                <span className="text-slate-400 ml-1.5">{s.secondary}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}